'use client';

import {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
  useCallback,
  Suspense,
  type ReactNode,
} from 'react';
import type {
  EngagementSignature,
  Question,
  Questionnaire,
} from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import { UserContext } from '@/_contexts/UserProvider';
import {
  getClientQuestionnaires,
  getQuestionnaire,
  answerQuestion,
  createCheckoutSession,
  markAsOpened,
} from '@/_api/client-questionnaires';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { ProfileReviewDialog } from './components/ProfileReviewDialog';
import { ContactMethodDialog } from './components/ContactMethodDialog';
import { ClientDashboardContext } from '@/_contexts/client/ClientDashboardProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { setDefaultCompany } from '@/_api/client-companies';
import { User } from '@/_types/models/user';
import { getTriggeredSections, getBlockedSections } from '@/_utils/sections';
import { usePrevious } from '@/_hooks/usePrevious';
import type { ClientQuestionnaireProviderProps } from './ClientQuestionnaireProvider.types';
import { sortClientQuestionnaires } from '@/_utils/questionnaire';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { removeGivenQuestionType } from '@/_utils/questions';
import { CookieValueTypes, getCookie } from 'cookies-next/client';
import { CONTACT_METHOD_PREFERENCE_COOKIE } from '@/_constants/cookies';

const ClientQuestionnaireContext =
  createContext<ClientQuestionnaireProviderProps>({
    createDepositCheckoutSession: () => null,
    filteredQuestionnaires: [],
    goToNextIncompleteSection: () => null,
    isDisabled: false,
    isLoading: false,
    isQuestionsLoading: false,
    isQuestionnairePending: false,
    isReviewed: false,
    loadSelectedQuestionnaireWithAllFields: () => null,
    questionnaireBlockedQuestions: [],
    questionnaires: [],
    reloadQuestionnaires: () => null,
    reloadQuestions: () => null,
    selectedQuestion: undefined,
    selectedQuestionnaire: undefined,
    selectedQuestionnaireWithAllFields: undefined,
    selectedQuestions: [],
    selectedSection: undefined,
    selectedSectionQuestions: [],
    selectedSections: [],
    selectedSignatures: [],
    setReviewed: () => null,
    setSelectedQuestion: () => null,
    setSelectedQuestions: () => null,
    setShowIncompleteNavigation: () => null,
    showIncompleteNavigation: true,
    updateParentQuestion: () => null,
    updateQuestion: () => null,
    updateSection: () => null,
  });

const ClientQuestionnaireProvider = (props: { children: ReactNode }) => {
  const [hasProfileReview, setProfileReview] = useState<boolean>(false);
  const [showIncompleteNavigation, setShowIncompleteNavigation] =
    useState<boolean>(false);
  const [showContactMethodDialog, setShowContactMethodDialog] =
    useState<boolean>(false);
  const [hasPendingUpdates, setHasPendingUpdates] = useState<boolean>(false);

  const { addToast } = useContext(ToastContext);
  const { yearFilter } = useContext(ClientDashboardContext);
  const {
    token,
    selectedCompanyId,
    selectedCompanyHash,
    setUser,
    user,
    isAdvisor,
  } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const { push } = useRouter();

  const pathname = usePathname();
  const sparams = useSearchParams();
  const questionnaireId = sparams.get('questionnaireId');
  const companyIdParam = sparams.get('companyId');
  const sectionId = sparams.get('sectionId');

  const [isReviewed, setReviewed] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isQuestionsLoading, setQuestionsLoading] = useState<boolean>(true);
  const [selectedQuestion, setSelectedQuestion] = useState<
    Question | undefined
  >(undefined);
  const [selectedQuestions, setSelectedQuestions] = useState<Array<Question>>(
    [],
  );
  const [selectedSections, setSelectedSections] = useState<Array<Section>>([]);
  const [questionnaires, setQuestionnaires] = useState<Array<Questionnaire>>(
    [],
  );
  const [
    selectedQuestionnaireWithAllFields,
    setSelectedQuestionnaireWithAllFields,
  ] = useState<Questionnaire | undefined>(undefined);

  const previousSelectedQuestions: Array<Question> =
    usePrevious(selectedQuestions);

  const _updateDefaultCompanyId = useCallback(async (): Promise<number> => {
    let localCompanyId: number = selectedCompanyId!;

    if (!questionnaireId) return localCompanyId;

    if (companyIdParam) {
      await setDefaultCompany({
        token,
        companyId: Number(companyIdParam),
      });

      localCompanyId = Number(companyIdParam);
    }

    setUser({ ...user, default_company_id: localCompanyId } as User);

    return localCompanyId;
  }, [
    companyIdParam,
    questionnaireId,
    selectedCompanyId,
    setUser,
    token,
    user,
  ]);

  const createDepositCheckoutSession = async () => {
    if (!selectedQuestionnaire || !token || typeof window === 'undefined') {
      return;
    }
    try {
      const response = await createCheckoutSession({
        id: selectedQuestionnaire.id,
        companyId: selectedQuestionnaire.company_id,
        token,
      });

      if (response?.checkout_url) {
        window.location.href = response.checkout_url; // Redirect to the checkout URL
      } else {
        console.error('Checkout URL not found in the response');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  // reset reviewed state on questionaire state change
  useEffect(() => {
    setReviewed(false);
  }, [questionnaireId]);

  const filteredQuestionnaires: Array<Questionnaire> = useMemo(() => {
    return !!yearFilter && questionnaires && questionnaires?.length
      ? questionnaires
          ?.filter?.((q) =>
            yearFilter ? q.tax_year === parseInt(yearFilter) : q,
          )
          ?.sort(sortClientQuestionnaires) || []
      : [];
  }, [questionnaires, yearFilter]);

  const onSetSelectedQuestion: any = (question?: Question) => {
    const originalRemovedCommentCount: number =
      question?.filtered_questionnaire_comments?.filter((x) => !x.removed)
        ?.length ?? 0;
    const removedCommentCount: number =
      selectedQuestion?.filtered_questionnaire_comments?.filter(
        (x) => !x.removed,
      )?.length ?? 0;

    const needsSync: boolean = Boolean(
      !question || originalRemovedCommentCount !== removedCommentCount,
    );

    // Only reload questionnaires if we have a selected company (i.e., we're not in admin-preview mode)
    if (needsSync && selectedCompanyId) {
      reloadQuestionnaires();
      reloadQuestions();
    }

    return setSelectedQuestion(question);
  };

  const filteredSections: Array<Section> = useMemo(
    () => getTriggeredSections(selectedSections, selectedQuestions),
    [selectedSections, selectedQuestions],
  );

  const selectedQuestionnaire: Questionnaire | undefined = useMemo(() => {
    const questionnaire = questionnaires?.find?.(
      (q) => q.id === parseFloat(questionnaireId ?? '-1'),
    );

    return questionnaire;
  }, [questionnaireId, questionnaires]);

  const isDisabled: boolean = useMemo(
    () => !!selectedQuestionnaire?.locked || !!selectedQuestionnaire?.completed,
    [selectedQuestionnaire],
  );

  const selectedSection = useMemo(() => {
    const fallbackSectionId: string =
      selectedSections?.[0]?.id.toString?.() ?? '-1';

    const section: Partial<Section> | undefined =
      questionnaires && questionnaires?.length
        ? questionnaires
            ?.flatMap((q) => q.sections)
            .find(
              (s: Section) =>
                s.id === parseFloat(sectionId ?? fallbackSectionId),
            )
        : undefined;

    const wideSelectedSection: Partial<Section> =
      selectedSections?.find(
        (s) => s.id === Number(sectionId ?? fallbackSectionId),
      ) || {};

    return { ...section, ...wideSelectedSection };
  }, [sectionId, questionnaires, selectedSections]);

  const selectedSectionQuestions = useMemo(
    () =>
      selectedQuestions?.filter(
        (q: Question) => q.section_id === selectedSection?.id,
      ),
    [selectedQuestions, selectedSection?.id],
  );

  const reloadQuestions = useCallback(async () => {
    if (!selectedCompanyId || !questionnaireId || !token || hasPendingUpdates) {
      return;
    }

    try {
      setQuestionsLoading(true);

      const localCompanyId = await _updateDefaultCompanyId();

      const response = await getQuestionnaire({
        companyId: localCompanyId!,
        questionnaireId: parseInt(questionnaireId)!,
        token,
      });

      const { update_profile_section, questionnaire_questions, sections } =
        response;

      // Show profile review modal with the flag
      if (update_profile_section) {
        setProfileReview(true);
      }

      setSelectedSections(sections);
      setSelectedQuestions(questionnaire_questions);
      setSelectedQuestionnaireWithAllFields(response);

      if (!response.opened_timestamp && !isAdvisor) {
        await markAsOpened({
          id: response.id,
          companyId: response.company_id,
          token,
        });
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setQuestionsLoading(false);
    }
  }, [
    addToast,
    questionnaireId,
    token,
    selectedCompanyId,
    _updateDefaultCompanyId,
    isAdvisor,
    hasPendingUpdates,
  ]);

  const loadSelectedQuestionnaireWithAllFields = useCallback(async (): Promise<
    Questionnaire | undefined
  > => {
    const companyId: string | number =
      selectedQuestionnaire?.company_id || (await _updateDefaultCompanyId());
    const questionnaireIdToPass: number | undefined | null =
      selectedQuestionnaire?.id || parseInt(questionnaireId as string);

    if (!companyId || !questionnaireIdToPass) return;

    const response = await getQuestionnaire({
      companyId,
      questionnaireId: questionnaireIdToPass,
      token,
    });

    const { questionnaire_questions, sections } = response;

    setSelectedSections(sections);
    setSelectedQuestions(questionnaire_questions);
    setSelectedQuestionnaireWithAllFields(response);

    return response;
  }, [_updateDefaultCompanyId, questionnaireId, selectedQuestionnaire, token]);

  const reloadQuestionnaires = useCallback(async () => {
    try {
      if (!token) return;

      setLoading(true);

      const localCompanyId = await _updateDefaultCompanyId();
      const questionnaires = await getClientQuestionnaires({
        companyId: localCompanyId!,
        token,
      });

      setQuestionnaires(questionnaires);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  }, [addToast, token, _updateDefaultCompanyId]);

  // Load questionnaires when user loaded
  useEffect(
    () => {
      selectedCompanyId && reloadQuestions();
      selectedCompanyId && reloadQuestionnaires();
    },
    // eslint-disable-next-line
    [selectedCompanyId, questionnaireId, token],
  );

  const _handleQuestionSavingError = (error?: string) => {
    if (!error) return;

    setSelectedQuestions(previousSelectedQuestions);
    addToast(
      'error',
      'Your answer couldn&apos;t be saved at this time. Please refresh and retry. Our team was notified.',
      { error },
    );
  };

  const updateQuestion = async ({
    questionId,
    answer,
    answerBy,
    companyId,
    isAsync,
  }: {
    questionId: number;
    answer: string;
    answerBy?: string | null;
    companyId?: number | null;
    isAsync?: boolean;
  }) => {
    let error: string | undefined;

    if (isAsync) setHasPendingUpdates(true);

    const newSelectedQuestions: Array<Question> = (
      selectedQuestions || []
    )?.map((q: Question) =>
      q.id === questionId ? { ...q, answer, answer_by: answerBy || null } : q,
    );

    const newSelectedSections: Array<Section> = selectedSections.map(
      (section) => {
        const updatedQuestionnaireQuestions: Array<Question> =
          section.questionnaire_questions?.map((q) => {
            return q.id === questionId
              ? { ...q, answer, answer_by: answerBy || null }
              : q;
          }) || [];

        section.questionnaire_questions = updatedQuestionnaireQuestions;

        return section;
      },
    );

    if (!isAsync) {
      setSelectedQuestions(newSelectedQuestions);
      setSelectedSections(newSelectedSections);
    }

    try {
      const { error: answerError } = await answerQuestion({
        companyId: companyId || selectedCompanyId!,
        answer,
        answerBy,
        questionId,
        token,
      });

      if (isAsync && !answerError) {
        setSelectedQuestions(newSelectedQuestions);
        setSelectedSections(newSelectedSections);
      }

      error = answerError;
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      if (isAsync) setHasPendingUpdates(false);
      _handleQuestionSavingError(error);
    }
  };

  const filteredSelectedQuestions: Array<Question> = useMemo(() => {
    const hasMoreSections: boolean = Boolean(
      (selectedSections?.length ?? 0) > 1,
    );
    const filteredQuestions: Array<Question> =
      hasMoreSections && preferences?.hide_document_uploads_within_sections
        ? removeGivenQuestionType(selectedQuestions, 'upload')
        : selectedQuestions;

    return filteredQuestions;
  }, [
    selectedQuestions,
    selectedSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const filteredSelectedSectionQuestions: Array<Question> = useMemo(() => {
    const hasMoreSections: boolean = Boolean(
      (selectedSections?.length ?? 0) > 1,
    );
    const filteredQuestions: Array<Question> =
      hasMoreSections && preferences?.hide_document_uploads_within_sections
        ? removeGivenQuestionType(selectedSectionQuestions, 'upload')
        : selectedSectionQuestions;

    return filteredQuestions;
  }, [
    selectedSectionQuestions,
    selectedSections?.length,
    preferences?.hide_document_uploads_within_sections,
  ]);

  const selectedSignatures: Array<EngagementSignature> = useMemo(() => {
    const isReviewSection: boolean = selectedSection?.section_type === 4;

    const activeSectionSignatures: Array<EngagementSignature> =
      selectedSections?.find((s: Section) => s.id === selectedSection?.id)
        ?.engagement_signatures || [];

    const allSignatures: Array<EngagementSignature> = selectedSections?.flatMap(
      (s: Section) => s.engagement_signatures || [],
    );

    return isReviewSection ? allSignatures : activeSectionSignatures;
  }, [selectedSections, selectedSection?.id, selectedSection?.section_type]);

  const updateSection = (
    sectionId: number,
    { marked_completed }: { marked_completed: boolean },
  ): void => {
    setSelectedSections((previous) =>
      previous.map((section) => {
        if (section?.id === sectionId) {
          section.marked_completed = marked_completed;
        }

        return section;
      }),
    );
  };

  const onCloseProfileReview = () => setProfileReview(!hasProfileReview);
  const onCloseContactMethodDialog = () => setShowContactMethodDialog(false);

  useEffect(() => {
    if (
      !!sparams.get('auth_token') ||
      pathname?.startsWith('/sign-in') ||
      pathname?.startsWith('/set-sign-in-method')
    ) {
      return setShowContactMethodDialog(false);
    }

    if (preferences?.sms_collection_popups_disabled) return;
    if (!user?.id || isAdvisor) return;

    const shouldShowDialog: boolean =
      !user?.phone_number ||
      user?.preferred_contact_method !== 'sms' ||
      !user?.preferred_contact_method;

    const hasContactMethodDialog: CookieValueTypes = getCookie(
      CONTACT_METHOD_PREFERENCE_COOKIE(user?.id),
    );

    if (!hasContactMethodDialog && shouldShowDialog) {
      setShowContactMethodDialog(true);
    }
  }, [
    isAdvisor,
    pathname,
    preferences?.sms_collection_popups_disabled,
    sparams,
    user?.id,
    user?.phone_number,
    user?.preferred_contact_method,
  ]);

  const updateParentQuestion = (question: Question) => {
    const updatedQuestions = selectedQuestions.map((q) => {
      if (q.id === question.id) {
        return question;
      }
      return q;
    });
    setSelectedQuestions(updatedQuestions);
    const updatedSections = selectedSections.map((s) => {
      if (s.id === question.section_id) {
        return { ...s, questionnaire_questions: updatedQuestions };
      }
      return s;
    });
    setSelectedSections(updatedSections);
  };

  // Align pending logic with UI when uploads are hidden within sections
  const sectionsForBlocking: Array<Section> = useMemo(() => {
    const hasMoreSections: boolean = Boolean(
      (selectedSections?.length ?? 0) > 1,
    );
    const shouldHideUploads: boolean = Boolean(
      hasMoreSections && preferences?.hide_document_uploads_within_sections,
    );

    if (!shouldHideUploads) return selectedSections;

    return (selectedSections || []).map((section) => ({
      ...section,
      questionnaire_questions: removeGivenQuestionType(
        section.questionnaire_questions || [],
        'upload',
      ),
    }));
  }, [selectedSections, preferences?.hide_document_uploads_within_sections]);

  const {
    isQuestionnairePending,
    hasBlockedQuestion,
    hasBlockedSection,
    questionnaireBlockedQuestions,
    firstBlockedSectionId,
    firstBlockedQuestion,
  } = getBlockedSections(sectionsForBlocking);

  const goToNextIncompleteSection = useCallback(() => {
    if (isQuestionnairePending) setShowIncompleteNavigation(true);

    if (preferences?.hide_document_uploads_within_sections) {
      const firstDocumentChecklistSection: Section | undefined =
        selectedSections?.find((s: Section) => s.section_type === 10);
      const firstBlockedUploadQuestion: Question | undefined = Object.values(
        questionnaireBlockedQuestions,
      )
        ?.flatMap((q) => q)
        ?.find((q) => q?.question_type === 'upload');

      if (
        typeof firstBlockedUploadQuestion?.id !== 'number' ||
        typeof firstDocumentChecklistSection?.id !== 'number'
      ) {
        return;
      }

      return (
        push(
          `/questionnaire/${selectedCompanyHash}?questionnaireId=${questionnaireId}&sectionId=${firstDocumentChecklistSection?.id}#question${firstBlockedUploadQuestion?.id}`,
        ),
        { scroll: false }
      );
    }

    if (hasBlockedSection) {
      return (
        push(
          `/questionnaire/${selectedCompanyHash}?questionnaireId=${questionnaireId}&sectionId=${firstBlockedSectionId}`,
        ),
        { scroll: false }
      );
    }

    if (!hasBlockedQuestion) return;

    return (
      push(
        `/questionnaire/${selectedCompanyHash}?questionnaireId=${questionnaireId}&sectionId=${firstBlockedQuestion?.section_id}#question${firstBlockedQuestion?.id}`,
      ),
      { scroll: false }
    );
  }, [
    preferences?.hide_document_uploads_within_sections,
    questionnaireBlockedQuestions,
    isQuestionnairePending,
    hasBlockedQuestion,
    hasBlockedSection,
    firstBlockedQuestion,
    firstBlockedSectionId,
    push,
    selectedSections,
    questionnaireId,
    selectedCompanyHash,
  ]);

  return (
    <Suspense>
      <ClientQuestionnaireContext
        value={{
          filteredQuestionnaires,
          goToNextIncompleteSection,
          isDisabled,
          isLoading,
          isQuestionsLoading,
          isQuestionnairePending,
          isReviewed,
          loadSelectedQuestionnaireWithAllFields,
          questionnaireBlockedQuestions,
          questionnaires,
          reloadQuestionnaires,
          reloadQuestions,
          selectedQuestion,
          selectedQuestionnaire,
          selectedQuestionnaireWithAllFields,
          selectedQuestions: filteredSelectedQuestions,
          selectedSection,
          selectedSectionQuestions: filteredSelectedSectionQuestions,
          selectedSections: filteredSections,
          selectedSignatures,
          setReviewed,
          setSelectedQuestion: onSetSelectedQuestion,
          setSelectedQuestions,
          setShowIncompleteNavigation,
          showIncompleteNavigation,
          updateQuestion,
          updateSection,
          updateParentQuestion,
          createDepositCheckoutSession,
        }}
      >
        <ProfileReviewDialog
          isOpen={hasProfileReview}
          onClose={onCloseProfileReview}
          questionnaireId={selectedQuestionnaire?.id!}
        />
        <ContactMethodDialog
          isOpen={showContactMethodDialog}
          onClose={onCloseContactMethodDialog}
        />
        {props.children}
      </ClientQuestionnaireContext>
    </Suspense>
  );
};

export { ClientQuestionnaireContext, ClientQuestionnaireProvider };
